import type {
    IPencilOfferInputs,
    IPencilOfferInputsIncentive,
    Offer,
    OfferType,
    PenciledInputOfferType,
    TermOverrides
} from '@makemydeal/dr-platform-types';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState, PriorLeaseBalance } from '../../../types/commonTypes';
import * as types from '../../../types/actionTypes';
import type { ReserveSplitInputs } from '../../../types/reserveSplitTypes';
import {
    clearFeesOverrides,
    clearIncentiveSelection,
    dropManualIncentivesFor,
    penciledInputsOverrides,
    reserveSplitInputsForLenderChange,
    resetPenciledInputsOverrides,
    updateInputWithValue,
    updateOfferPrice,
    updateOfferTypeWithInputs
} from '../helpers';
import type { vehicleInfoPayload } from '../../../actions/creators';
import {
    isCashDealIncentivesFixesEnabled,
    isProgramIncentivesRestorationEnabled,
    updateTermIncentives
} from '../../../utils/incentives';
import {
    buildDueAtSigningPenciledInputsState,
    buildRollToPenciledInputsState,
    buildLeaseContractFieldsPenciledInputsState,
    changesAnnualMiles
} from '../../../utils';
import isEmpty from 'lodash.isempty';
import { getDealerDiscountsTotalAmount, getMileageChangeResidualKeeper } from '../../../selectors';

const selectedTermMonthsOf = (offer: Offer, offerType: OfferType): number => {
    const selectedTermId = offer.penciledInputs?.byOfferType?.[offerType]?.term;
    return offer.terms?.byId?.[selectedTermId!]?.months || 0;
};

const withoutResidualOverrides = (
    overridesByTerm: IPencilOfferInputs['overridesByTerm'],
    dealTermMonths: number,
    offer: Offer,
    offerType: OfferType
): Record<string | number, TermOverrides> => {
    const { keepsTerm } = getMileageChangeResidualKeeper({ offer }, offerType);
    return Object.fromEntries(
        Object.entries({ ...overridesByTerm, [dealTermMonths]: overridesByTerm?.[dealTermMonths] }).map(
            ([termMonths, termOverrides]): [string, TermOverrides] => [
                termMonths,
                { ...termOverrides, ...(!keepsTerm(Number(termMonths)) && { residualInfo: undefined }) }
            ]
        )
    );
};

export const handleOverrideCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    offer: Offer,
    isUserProgramQuotes: boolean,
    isPaymentGridRedesignEnabled = false
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.UPDATED_RETAIL_PRICE_OVERRIDE: {
            const retailPrice = action.payload;

            const retailPriceOverrideUpdates = updateOfferTypeWithInputs(
                state.byOfferType,
                updateInputWithValue(state.byOfferType?.[offerType], 'retailPriceOverride', retailPrice),
                offerType
            );

            Object.keys(state.byOfferType!).forEach((nonPrimaryOfferType) => {
                if (nonPrimaryOfferType !== offerType) {
                    (retailPriceOverrideUpdates as any)[nonPrimaryOfferType] = {
                        ...(retailPriceOverrideUpdates as any)[nonPrimaryOfferType],
                        retailPriceOverride: retailPrice,
                        // Preserve existing isUpdatedByUser flag for non-primary offer types
                        isUpdatedByUser:
                            state.byOfferType?.[nonPrimaryOfferType as keyof typeof state.byOfferType]?.isUpdatedByUser || false
                    };
                }
            });

            return {
                ...state,
                byOfferType: retailPriceOverrideUpdates
            };
        }

        case types.UPDATED_VEHICLE_INFO_DETAILS: {
            const { retailPrice, sellingPrice } = action.payload as vehicleInfoPayload;
            const updatedPayload: PartialPenciledInputsWithAllTypes = { ...state };
            if (retailPrice !== undefined) {
                Object.keys(state.byOfferType!).forEach((offerTypeName) => {
                    if (offerTypeName === offerType) {
                        updatedPayload.byOfferType![offerTypeName].retailPriceOverride = retailPrice;
                    } else if (state.byOfferType?.[offerTypeName as keyof typeof state.byOfferType].retailPriceOverride == null) {
                        (
                            updatedPayload.byOfferType![offerTypeName as keyof typeof state.byOfferType] as IPencilOfferInputs
                        ).retailPriceOverride = offer.vehicle.retailPrice;
                    }
                });
                updatedPayload.byOfferType![offerType].isUpdatedByUser = true;
            }

            if (sellingPrice !== undefined) {
                updatedPayload.byOfferType![offerType].offerPrice = sellingPrice;
                updatedPayload.byOfferType![offerType].isUpdatedByUser = true;
            }

            return updatedPayload;
        }

        case types.UPDATED_SECURITY_DEPOSIT_OVERRIDE: {
            const securityDepositAmount = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'securityDepositOverride', securityDepositAmount);
            const securityDepositOverride = updateOfferTypeWithInputs(state.byOfferType, inputs, offerType);

            return {
                ...state,
                byOfferType: securityDepositOverride
            };
        }

        case types.UPDATED_MAX_RESIDUALIZED_MSRP_OVERRIDE: {
            const { maxResidualizedMsrp, maxResidualizedMsrpApplied } = action.payload;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        maxResidualizedMsrp,
                        maxResidualizedMsrpApplied
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.UPDATED_ACQFEE_UPFRONT_OVERRIDE:
        case types.UPDATED_ACQFEE_OVERRIDE: {
            const { amount, upFront } = action.payload;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        acqFeeOverride: amount,
                        acqFeeUpFrontOverride: upFront
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.UPDATED_RESERVE_SPLIT_OVERRIDE: {
            const reserveSplit = action.payload;
            const prevInputs: ReserveSplitInputs | undefined = state.byOfferType?.[offerType];
            const reserveSplitInputs: ReserveSplitInputs = {
                isReserveSplitUserEntered: reserveSplit != null,
                ...(prevInputs?.isReserveSplitClearedByLenderChange && {
                    isReserveSplitClearedByLenderChange: false,
                    reserveSplitClearedFrom: undefined
                })
            };
            const inputs = { ...updateInputWithValue(prevInputs, 'reserveSplitOverride', reserveSplit), ...reserveSplitInputs };
            const reserveSplitOverride = updateOfferTypeWithInputs(state.byOfferType, inputs, offerType);
            return {
                ...state,
                byOfferType: reserveSplitOverride
            };
        }

        case types.CLEARED_RESERVE_SPLIT_LENDER_CHANGE_MESSAGE: {
            const dismissedMessage: ReserveSplitInputs = {
                isReserveSplitClearedByLenderChange: false,
                reserveSplitClearedFrom: undefined
            };
            const inputs = { ...state.byOfferType?.[offerType], ...dismissedMessage };
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.UPDATED_MILEAGE_CHARGE_OVERRIDE: {
            const mileageCharge = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'mileageChargeOverride', mileageCharge);
            const mileageChargeOverride = updateOfferTypeWithInputs(state.byOfferType, inputs, offerType);
            return {
                ...state,
                byOfferType: mileageChargeOverride
            };
        }

        case types.UPDATED_INCENTIVES_TOTAL_OVERRIDE: {
            const incentiveTotal = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'incentiveTotalOverride', incentiveTotal);
            const incentiveTotalOverride = updateOfferTypeWithInputs(state.byOfferType, inputs, offerType);
            return {
                ...state,
                byOfferType: incentiveTotalOverride
            };
        }

        case types.UPDATED_DOWN_PAYMENT_IMMEDIATE: {
            const downPaymentImmediate = action.payload;
            const offerT = action.meta.middleware.offerType || offerType;
            const inputs = updateInputWithValue(
                state.byOfferType?.[offerT as keyof typeof state.byOfferType],
                'downPayment',
                downPaymentImmediate
            );
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerT)
            };
        }

        case types.UPDATED_CREDIT_DECISION_LENDER_NAME: {
            const newLenderName = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'creditDecisionLenderName', newLenderName);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.UPDATED_CREDIT_DECISION_LENDER: {
            const newLender = action.payload;
            const prevInputs = state.byOfferType?.[offerType];
            const inputs = {
                ...updateInputWithValue(prevInputs, 'creditDecisionLender', newLender),
                ...(action.meta?.isReserveSplitEnabled &&
                    reserveSplitInputsForLenderChange(prevInputs, newLender, action.meta?.lenderChangeOrigin))
            };
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.UPDATE_CREDIT_DECISION_LENDER_WITH_ADDRESS: {
            const newLender = action.payload.lender;
            const prevInputs = state.byOfferType?.[offerType as keyof typeof state.byOfferType];
            const inputs = {
                ...updateInputWithValue(prevInputs, 'creditDecisionLender', newLender),
                ...(action.meta?.isReserveSplitEnabled &&
                    reserveSplitInputsForLenderChange(prevInputs, newLender, action.meta?.lenderChangeOrigin))
            };
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.UPDATE_TITLING_LENDER_WITH_ADDRESS: {
            const newTitlingLender = action.payload.titlingLender;
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'titlingLender', newTitlingLender);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.UPDATED_CREDIT_DECISION_STATUS: {
            const updatedStatus = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'creditDecisionStatus', updatedStatus);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.UPDATE_DEAL_DEPOSIT: {
            const dealDeposit = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[CASH], 'dealDeposit', dealDeposit);
            const dealDepositCashOfferTypeInput = updateOfferTypeWithInputs(state.byOfferType, inputs, CASH);
            return {
                ...state,
                byOfferType: dealDepositCashOfferTypeInput
            };
        }

        case types.SET_OUTSIDE_LIENHOLDER: {
            const outsideLienholder = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[CASH], 'lienHolder', outsideLienholder);
            const outsideLienholderCashOfferTypeInput = updateOfferTypeWithInputs(state.byOfferType, inputs, CASH);
            return {
                ...state,
                byOfferType: outsideLienholderCashOfferTypeInput
            };
        }

        case types.SET_TRANSFER_REGISTRATION_DETAILS: {
            const { registrationType, registrationExpiresOn } = action.payload;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        registrationType,
                        registrationExpiresOn
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.SET_PRIOR_LEASE_BALANCE: {
            const priorLeaseBalance = action.payload as PriorLeaseBalance | undefined;

            if (!priorLeaseBalance || isEmpty(priorLeaseBalance)) {
                const { priorLeaseBalance: _cleared, ...withoutPriorLeaseBalance } = state;
                return withoutPriorLeaseBalance as PartialPenciledInputsWithAllTypes;
            }

            return {
                ...state,
                priorLeaseBalance
            };
        }

        case types.UPDATED_MANUAL_CREDIT_SCORE: {
            const score = action.payload;

            return {
                ...state,
                manualCreditScore: score
            };
        }

        case types.SELECTED_ANNUAL_MILES: {
            const miles = action.payload;
            const months = selectedTermMonthsOf(offer, offerType);
            const previousOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm;
            const overridesByTerm = isPaymentGridRedesignEnabled
                ? withoutResidualOverrides(previousOverridesByTerm, months, offer, offerType)
                : undefined;

            if (!isUserProgramQuotes) {
                // If we are not in program quotes, we need to update the annual miles
                return {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...state.byOfferType?.[offerType],
                            annualMiles: miles,
                            // DE766084: seeded from the program's `excessMileageCharge`, so it described the
                            // mileage being left. Only on program-quotes - in Freehand the value is the user's
                            // and AC2 keeps it until they change it again.
                            mileageChargeOverride: undefined,
                            overridesByTerm: overridesByTerm ?? {
                                ...previousOverridesByTerm,
                                [months]: {
                                    ...previousOverridesByTerm?.[months],
                                    residualInfo: undefined
                                }
                            }
                        }
                    } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
                };
            }
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'annualMiles', miles);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(
                    state.byOfferType,
                    overridesByTerm ? { ...inputs, overridesByTerm } : inputs,
                    offerType
                )
            };
        }

        case types.REQUEST_LEASE_CONTRACT_FIELDS: {
            const nextState = buildLeaseContractFieldsPenciledInputsState(state, action.payload, offerType);
            if (!isPaymentGridRedesignEnabled || !changesAnnualMiles(action)) return nextState;

            const inputs = nextState.byOfferType?.[offerType];
            return {
                ...nextState,
                byOfferType: {
                    ...nextState.byOfferType,
                    [offerType]: {
                        ...inputs,
                        overridesByTerm: withoutResidualOverrides(
                            inputs?.overridesByTerm,
                            selectedTermMonthsOf(offer, offerType),
                            offer,
                            offerType
                        )
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.APPLY_ROLL_TO_OPTIONS: {
            const adjustedValues = action.payload.options;

            return buildRollToPenciledInputsState(state, adjustedValues, offerType, { logRefusals: true });
        }

        case types.APPLY_DUE_AT_SIGNING: {
            return buildDueAtSigningPenciledInputsState(state, action.payload, offerType);
        }

        case types.CLEAR_MANUAL_CREDIT_SCORE: {
            return { ...state, manualCreditScore: undefined };
        }

        case types.SELECTED_CREDIT_TIER: {
            const id = action.payload;
            // update finance creditTier
            const financeInputs = updateInputWithValue(state.byOfferType?.[FINANCE], 'creditTier', id);
            const finance = {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, financeInputs, FINANCE)
            };
            // update lease creditTier
            const leaseInputs = updateInputWithValue(state.byOfferType?.[LEASE], 'creditTier', id);
            const lease = {
                ...finance,
                byOfferType: updateOfferTypeWithInputs(finance.byOfferType, leaseInputs, LEASE)
            };
            return {
                ...state,
                byOfferType: {
                    finance: lease.byOfferType.finance,
                    lease: lease.byOfferType.lease,
                    balloon: lease.byOfferType.balloon,
                    cash: state.byOfferType?.cash as IPencilOfferInputs
                }
            };
        }

        case types.UPDATED_TRADE_IN: {
            const tradeIn = action.payload;
            const financeTradeInInputs = updateInputWithValue(state.byOfferType?.[FINANCE], 'tradeIn', tradeIn);
            const financeTradeIn = {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, financeTradeInInputs, FINANCE)
            };
            const leaseTradeInInputs = updateInputWithValue(state.byOfferType?.[LEASE], 'tradeIn', tradeIn);
            const leaseTradeIn = {
                ...financeTradeIn,
                byOfferType: updateOfferTypeWithInputs(financeTradeIn.byOfferType, leaseTradeInInputs, LEASE)
            };

            let updatedCashTrade = {};
            if (!isEmpty(state?.byOfferType?.cash)) {
                const cashTradeInInputs = updateInputWithValue(state.byOfferType?.[CASH], 'tradeIn', tradeIn);
                const cashTradeIn = {
                    ...financeTradeIn,
                    byOfferType: updateOfferTypeWithInputs(financeTradeIn.byOfferType, cashTradeInInputs, CASH)
                };
                updatedCashTrade = cashTradeIn.byOfferType.cash;
            }

            return {
                allTypes: state.allTypes,
                byOfferType: {
                    finance: leaseTradeIn.byOfferType.finance,
                    lease: leaseTradeIn.byOfferType.lease,
                    balloon: leaseTradeIn.byOfferType.balloon,
                    cash: updatedCashTrade
                }
            };
        }

        case types.UPDATE_OVERRIDES: {
            const offerType = action.payload;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[action.payload as keyof typeof state.byOfferType],
                        ...penciledInputsOverrides(state, offer, offerType, offerType)
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.SWITCH_VEHICLE: {
            const listedPrice = action.payload.listedPrice;
            const penciledInputsDealerDiscountsTotal = getDealerDiscountsTotalAmount(state) || 0;
            let updateSwitchVehicleOffer = updateOfferPrice(state.byOfferType, listedPrice - penciledInputsDealerDiscountsTotal, [
                FINANCE,
                LEASE,
                CASH
            ]);
            if (isProgramIncentivesRestorationEnabled(offer)) {
                updateSwitchVehicleOffer = {
                    ...updateSwitchVehicleOffer,
                    ...resetPenciledInputsOverrides(updateSwitchVehicleOffer, false)
                };
                updateSwitchVehicleOffer = clearIncentiveSelection(
                    updateSwitchVehicleOffer,
                    Boolean(action.meta?.preserveIncentiveSelection)
                );
                // Cash has no per-lender programs to re-derive incentives from after a vehicle switch
                // (unlike finance/lease, where a fresh term-based program selection replaces them), so a
                // user's manual incentives from the old vehicle would otherwise carry over unchanged
                // instead of being re-entered against the new vehicle. Scoped to switches made while on
                // a cash deal - a switch made from finance/lease must not touch cash's dormant bucket.
                updateSwitchVehicleOffer = dropManualIncentivesFor(
                    updateSwitchVehicleOffer,
                    offerType === CASH && isCashDealIncentivesFixesEnabled(offer) ? [CASH] : []
                );
            } else {
                const preserveManualIncentives = Boolean(action.meta?.preserveManualIncentives);
                updateSwitchVehicleOffer = {
                    ...updateSwitchVehicleOffer,
                    ...resetPenciledInputsOverrides(updateSwitchVehicleOffer, !preserveManualIncentives)
                };
            }

            return {
                ...state,
                byOfferType: action.meta?.clearFeesOverride
                    ? clearFeesOverrides(updateSwitchVehicleOffer)
                    : updateSwitchVehicleOffer
            };
        }

        case types.UPDATE_AVAILABLE_INCENTIVES: {
            // Pre-existing, ungated route: ShopperPlatform still dispatches this global type with a
            // thin { id, amount } cash node (client/src/shells/main/middleware/offer/offer.ts), and
            // eight selectors already depend on what this branch writes. Left exactly as on master -
            // the cash-deal-incentives-fixes toggle only governs our own new UPDATE_CASH_AVAILABLE_INCENTIVES
            // type below, so this consumer isn't affected by that toggle either way.
            const incentives = {
                conditionals: [] as IPencilOfferInputsIncentive[],
                nonConditionals: [] as IPencilOfferInputsIncentive[]
            };

            const newIncentives = action.payload.cash?.incentives || {};
            newIncentives.conditionals?.forEach((incentive: any) => {
                if (!isEmpty(incentive) && incentive.id) {
                    incentives.conditionals.push({
                        id: `conditional_cash_${incentive.id}`,
                        amount: incentive.amount
                    });
                }
            });

            newIncentives.nonConditionals?.forEach((incentive: any) => {
                if (!isEmpty(incentive) && incentive.id) {
                    incentives.nonConditionals.push({
                        id: `nonconditional_cash_${incentive.id}`,
                        amount: incentive.amount
                    });
                }
            });

            const cash = {
                ...state.byOfferType?.cash,
                incentives
            };

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [CASH]: cash
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.UPDATE_CASH_AVAILABLE_INCENTIVES: {
            // Gated like every other cash-deal-incentives-fixes branch in this file: this action is
            // only ever dispatched by our own (already-gated) middleware today, but offer-redux is
            // published, so an external dispatcher shouldn't be able to change the cash catalog shape
            // without the toggle.
            if (!isCashDealIncentivesFixesEnabled(offer)) return state;

            const newIncentives = action.payload.cash?.incentives || {};
            // includeDetails=true keeps exclusions (needed for mutual-exclusion enforcement, DE759607)
            // plus name/programId/incentiveType, matching the finance/lease term-incentive shape.
            const incentives = {
                conditionals: updateTermIncentives(
                    newIncentives.conditionals,
                    'conditional_cash_',
                    true
                ) as IPencilOfferInputsIncentive[],
                nonConditionals: updateTermIncentives(
                    newIncentives.nonConditionals,
                    'nonconditional_cash_',
                    true
                ) as IPencilOfferInputsIncentive[]
            };

            const cash = {
                ...state.byOfferType?.cash,
                incentives,
                incentivesContext: action.payload.cash?.incentivesContext
            };

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [CASH]: cash
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.UPDATE_IS_TARGET_OUT_OF_POCKET: {
            const isTargetOutOfPocket = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[LEASE], 'isTargetOutOfPocket', isTargetOutOfPocket);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, LEASE)
            };
        }
        case types.UPDATE_DEFERRED_DOWN_PAYMENT: {
            const deferredDownPayment = action.payload;
            const inputs = updateInputWithValue(state.byOfferType?.[FINANCE], 'deferredDownPayment', deferredDownPayment);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, FINANCE)
            };
        }

        default: {
            return state;
        }
    }
};
